import React, { useEffect, useState } from "react";
import Icon from "../../../components/Icon";
import PanelTemplate from "../../../templates/Panel";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import InputSelect from "../../../components/InputSelect";
import { toast } from "react-toastify";
import { getLabelFromOptions } from "../../../helpers/selects";
import { convertDateToRelativeTime, convertIntToMoney, convertMoenyToInt } from "../../../helpers/conversions";
import InputDate from "../../../components/InputDate";
import SelectCompany from "../../../components/selects/SelectCompany";
import SelectCustomer from "../../../components/selects/SelectCustomer";
import SelectAdmin from "../../../components/selects/SelectAdmin";
import Avatar from "../../../components/Avatar";
import CRMTimeline from "./Timeline";
import CRMTasks from "./Tasks";
import statusOptions from "../../../seeds/crm/status";
import { ModalDelete } from "../../../components/ModalDelete";
import CardTagsField from "./CardTagsField";

const ACTIVITY_TABS = [
    { key: 'timeline', label: 'Timeline', icon: 'activity-comments' },
    { key: 'tasks', label: 'Tarefas', icon: 'task-task-pending' },
];

export const CRMCard = () => {
    const { id } = useParams();

    const navigate = useNavigate();
    const location = useLocation();

    const [gettingCard, setGettingCard] = useState(true);
    const [updatingCard, setUpdatingCard] = useState(false);
    const [card, setCard] = useState(undefined);

    const [gettingCompany, setGettingCompany] = useState(true);
    const [company, setCompany] = useState(null);

    const [gettingCustomer, setGettingCustomer] = useState(true);
    const [customer, setCustomer] = useState(null);

    const [confirmDelete, setConfirmDelete] = useState(false);
    const [activityTab, setActivityTab] = useState('timeline');

    const [tags, setTags] = useState([]);

    const loadTags = () => api.get('/crm-tag').then(res => {
        setTags(res.data.resources ?? []);
        return res.data.resources ?? [];
    }).catch(error => {
        renderError(error);
        return [];
    });

    useEffect(() => {
        if(id){
            getCard();
        }
    }, [id]);

    useEffect(() => {
        loadTags();
    }, []);

    useEffect(() => {
        if(card){
            getCompany();
            getCustomer();
        }
    }, [card]);

    const getCustomer = async () => {
        if(customer?.id !== card?.customer?.id){
            setGettingCustomer(true);
            await api.get(`/customer/${card.customer.id}`).then(res => {
                setCustomer(res.data.resource);
                setGettingCustomer(false);
            });
        }

        setGettingCustomer(false);
    }

    const getCompany = async () => {
        if(company?.id !== card?.company?.id){
            setGettingCompany(true);
            await api.get(`/company/${card.company.id}`).then(res => {
                setCompany(res.data.resource);
                setGettingCompany(false);
            });

        }

        setGettingCompany(false);
    }

    const getCard = () => {
        setGettingCard(true);

        api.get(`/pipeline-stage-card/${id}`).then(res => {
            setCard(res.data.resource);
        }).catch(error => {
            renderError(error);
        }).then(() => setGettingCard(false));
    }

    const updateCardParam = async (param, value) => {
        setUpdatingCard(true);

        if(param === 'amount'){
            value = convertMoenyToInt(value);
        }

        let updatedCard = {
            ...card,
            [param]: value
        };

        return api.put(`/pipeline-stage-card/${card.id}`, updatedCard).then(res => {
            setCard(res.data.resource);
            return true;
        }).catch(error => {
            renderError(error);
            return false;
        }).then(result => {
            setUpdatingCard(false);
            return result;
        });
    }

    const updateCompanyParam = async (param, value) => {
        let updatedCompany = {
            ...company,
            [param]: value
        };

        return api.put(`/company/${company.id}`, updatedCompany).then(res => {
            setCompany(res.data.resource);
            return true;
        }).catch(error => {
            renderError(error);
            return false;
        });
    }

    const updateCustomerParam = async (param, value) => {
        // O GET /customer retorna `companies` (objetos) e não `company_ids`, e o
        // marcador (`type`) pode vir vazio. O backend (CustomerRequest) exige
        // `company_ids` presente e `type` obrigatório, então montamos o payload
        // como a tela de edição: derivamos os ids das empresas e herdamos os
        // marcadores das empresas vinculadas.
        const companies = customer.companies ?? [];
        const type = [...new Set([
            ...(customer.type ?? []),
            ...companies.flatMap(company => company.type ?? [])
        ])];

        let updatedCustomer = {
            ...customer,
            company_ids: companies.map(company => company.id),
            type,
            [param]: value
        };

        return api.put(`/customer/${customer.id}`, updatedCustomer).then(res => {
            setCustomer(res.data.resource);
            return true;
        }).catch(error => {
            renderError(error);
            return false;
        });
    }

    // Ganho/Perdido dependem da pipeline, não da origem do card: pipelines "sem
    // resultado comercial" (card_status_mode = operational) só trabalham com
    // Aberto e Arquivado.
    const allowsCommercialResult = card?.pipeline_card_status_mode !== 'operational';

    // Volta pra pipeline restaurando-o exatamente como estava: quando o card foi
    // aberto a partir da pipeline (state `from: 'crm-board'`), usamos o histórico
    // (navigate(-1)) — a URL volta com os filtros e a pipeline restaura paginação
    // e scroll salvos no sessionStorage. Senão, navegamos direto pra pipeline.
    const backToBoard = () => {
        if (location.state?.from === 'crm-board') {
            navigate(-1);
            return;
        }

        navigate(card?.pipeline_id ? `/crm/pipeline/${card.pipeline_id}` : '/crm');
    }

    const deleteCard = () => {
        setUpdatingCard(true);

        api.delete(`/pipeline-stage-card/${card.id}`).then(res => {
            navigate(card?.pipeline_id ? `/crm/pipeline/${card.pipeline_id}` : '/crm');
        }).catch(error => {
            renderError(error);
        }).then(() => setUpdatingCard(false))
    }

    const currentStage = card?.stages?.find(stage => stage.is_current);
    const heroTitle = card?.company?.trade_name || card?.customer?.name || card?.label || (card ? `Oportunidade #${card.code}` : '');
    const heroMeta = card ? [
        `#${card.code}`,
        card.company?.trade_name && card.customer?.name ? card.customer.name : null,
        [card.company?.city, card.company?.state].filter(Boolean).join('/') || null,
        currentStage ? `${currentStage.name} há ${currentStage.days_in_stage} dia${currentStage.days_in_stage === 1 ? '' : 's'}` : null,
    ].filter(Boolean) : [];

    return (
        <PanelTemplate id="crm-card">
            <ModalDelete
                show={confirmDelete}
                action={deleteCard}
                close={() => setConfirmDelete(false)}
                loading={updatingCard}
            />
            <nav className="card-crumbs">
                <button type="button" onClick={backToBoard}>
                    <Icon name="arrow" size={14} className="crumb-arrow" />
                    Pipeline
                </button>
                <span className="crumb-sep">/</span>
                <span className="crumb-current">{card ? `Oportunidade #${card.code}` : 'Carregando...'}</span>
            </nav>

            {card &&
                <header className="card-hero">
                    <div className="hero-row">
                        <div className="hero-identity">
                            <Avatar name={heroTitle} size={46} />
                            <div className="hero-heading">
                                <h1>
                                    {heroTitle}
                                    {card.status !== 'open' &&
                                        <span className={`hero-status is-${card.status}`}>
                                            {getLabelFromOptions(statusOptions, card.status)}
                                        </span>
                                    }
                                </h1>
                                <div className="hero-meta">
                                    {heroMeta.map((item, index) => (
                                        <span key={index}>{item}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="hero-actions">
                            {updatingCard &&
                                <span className="hero-saving">Salvando...</span>
                            }
                            {!updatingCard && card.status === 'open' &&
                                <>
                                    {allowsCommercialResult &&
                                        <>
                                            <Button
                                                type={`primary`}
                                                text={`Ganho`}
                                                size={`small`}
                                                svg={`check`}
                                                btnClass={`hero-btn hero-btn-won`}
                                                action={() => updateCardParam('status', 'won')}
                                                loading={updatingCard}
                                            />
                                            <Button
                                                type={`secondary`}
                                                text={`Perdido`}
                                                size={`small`}
                                                btnClass={`hero-btn hero-btn-lost`}
                                                action={() => updateCardParam('status', 'lost')}
                                                loading={updatingCard}
                                            />
                                        </>
                                    }
                                    <Button
                                        type={`secondary`}
                                        text={`Arquivar`}
                                        size={`small`}
                                        btnClass={`hero-btn hero-btn-archive`}
                                        action={() => updateCardParam('status', 'archived')}
                                        loading={updatingCard}
                                    />
                                </>
                            }
                            {!updatingCard && card.status !== 'open' &&
                                <Button
                                    type={`primary`}
                                    text={`Reabrir oportunidade`}
                                    size={`small`}
                                    btnClass={`hero-btn`}
                                    action={() => updateCardParam('status', 'open')}
                                />
                            }
                            {!updatingCard &&
                                <button
                                    type="button"
                                    className="hero-delete"
                                    title="Excluir oportunidade"
                                    onClick={() => setConfirmDelete(true)}
                                >
                                    <Icon name="delete" size={16} />
                                </button>
                            }
                        </div>
                    </div>

                    <ol className="stage-rail">
                        {(() => {
                            const currentIndex = card.stages.findIndex(s => s.is_current);
                            return card.stages.map((stage, index) => {
                                const state = stage.is_current
                                    ? 'active'
                                    : (currentIndex > -1 && index < currentIndex) ? 'done' : 'upcoming';
                                return (
                                    <li key={stage.id ?? index} className={`stage-step is-${state}`}>
                                        <button
                                            type="button"
                                            onClick={() => updateCardParam('pipeline_stage_id', stage.id)}
                                            title={`Mover para ${stage.name}`}
                                            disabled={updatingCard || stage.is_current}
                                        >
                                            <span className="step-marker">
                                                {state === 'done' ? <Icon name="check" size={13} strokeWidth={3} /> : index + 1}
                                            </span>
                                            <span className="step-name">{stage.name}</span>
                                            <span className="step-days">
                                                {stage.days_in_stage > 0
                                                    ? `${stage.days_in_stage} dia${stage.days_in_stage === 1 ? '' : 's'}`
                                                    : (state === 'upcoming' ? 'a seguir' : 'hoje')}
                                            </span>
                                        </button>
                                    </li>
                                );
                            });
                        })()}
                    </ol>
                </header>
            }

            {card &&
                <div className="card-grid">
                    <main className="card-main">
                        <section className="activity-panel">
                            <div className="activity-tabs" role="tablist">
                                {ACTIVITY_TABS.map(tab => (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        role="tab"
                                        aria-selected={activityTab === tab.key}
                                        className={activityTab === tab.key ? 'active' : ''}
                                        onClick={() => setActivityTab(tab.key)}
                                    >
                                        <Icon name={tab.icon} size={15} />
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                            <div className="activity-body">
                                {activityTab === 'timeline' && <CRMTimeline cardId={card.id} />}
                                {activityTab === 'tasks' && <CRMTasks cardId={card.id} />}
                            </div>
                        </section>
                    </main>

                    <aside className="card-aside">
                        {(card.related_order || card.order_item)
                            ? <OrderOrigin order={card.related_order} item={card.order_item} />
                            : card.budget && <QuoteOrigin budget={card.budget} />
                        }

                        <AsideSection icon="notebook-pen" title="Negociação">
                            <PropRow
                                label={`Código`}
                                previewValue={card.code}
                            />
                            <PropRow
                                type="admin"
                                label={`Responsável`}
                                previewValue={
                                    card?.admin
                                        ? <span className="prop-person"><Avatar name={card.admin.name} photo={card.admin.photo_url} size={20} /> {card.admin.name}</span>
                                        : <span className="prop-muted">Sem responsável</span>
                                }
                                copyValue={card?.admin?.name ?? 'Sem responsável'}
                                value={card?.admin?.id ?? null}
                                pipelineId={card?.pipeline_id ?? null}
                                change={async value => updateCardParam('admin_id', value)}
                            />
                            <CardTagsField
                                card={card}
                                tags={tags}
                                onCardChange={setCard}
                                onTagCreated={loadTags}
                            />
                            {!card.budget &&
                                <PropRow
                                    label={`Valor`}
                                    previewValue={<strong className="prop-amount">{convertIntToMoney(card.amount, true)}</strong>}
                                    copyValue={convertIntToMoney(card.amount, true)}
                                    value={card.amount}
                                    change={async value => updateCardParam('amount', value)}
                                    mask={'convertToMoney'}
                                />
                            }
                            <PropRow
                                type="date"
                                label={`Previsão`}
                                previewValue={card.closing_date ? convertDateToRelativeTime(card.closing_date) : <span className="prop-muted">Não informada</span>}
                                copyValue={card.closing_date ? convertDateToRelativeTime(card.closing_date) : 'Não informada'}
                                value={card.closing_date}
                                change={async value => updateCardParam('closing_date', value)}
                            />
                            <PropRow
                                label={`Criado em`}
                                previewValue={convertDateToRelativeTime(card.created_at, true)}
                            />
                            <PropRow
                                label={`Atualizado`}
                                previewValue={convertDateToRelativeTime(card.updated_at, true)}
                            />
                        </AsideSection>

                        <AsideSection
                            icon="company"
                            title="Empresa"
                            meter={company?.filled_percentage}
                        >
                            <div className="aside-picker">
                                <SelectCompany
                                    key={company?.updated_at ?? 'company'}
                                    selectedCompany={card?.company?.id ?? null}
                                    setSelectedCompany={value => updateCardParam('company_id', value)}
                                />
                            </div>
                            {(gettingCompany || gettingCard) &&
                                <p className="aside-loading">Carregando dados...</p>
                            }
                            {!gettingCompany && !gettingCard && company &&
                                <>
                                    <PropRow
                                        label={`Nome fantasia`}
                                        previewValue={company.trade_name}
                                        value={company.trade_name}
                                        change={async value => updateCompanyParam('trade_name', value)}
                                    />
                                    <PropRow
                                        label={`Razão social`}
                                        previewValue={company.company_name}
                                        value={company.company_name}
                                        change={async value => updateCompanyParam('company_name', value)}
                                    />
                                    <PropRow
                                        label={`Telefone`}
                                        previewValue={company.phone}
                                        value={company.phone}
                                        change={async value => updateCompanyParam('phone', value)}
                                        mask={`convertToPhone`}
                                    />
                                    <Link className="aside-more" to={`/company/edit/${company.id}`}>
                                        Ficha completa da empresa
                                        <Icon name="arrow" size={14} />
                                    </Link>
                                </>
                            }
                        </AsideSection>

                        <AsideSection
                            icon="card-user"
                            title="Pessoa"
                            meter={customer?.filled_percentage}
                        >
                            <div className="aside-picker">
                                <SelectCustomer
                                    key={customer?.updated_at ?? 'customer'}
                                    selectedCustomer={card?.customer?.id ?? null}
                                    setSelectedCustomer={value => updateCardParam('customer_id', value)}
                                    companyId={null}
                                />
                            </div>
                            {(gettingCustomer || gettingCard) &&
                                <p className="aside-loading">Carregando dados...</p>
                            }
                            {!gettingCustomer && !gettingCard && customer &&
                                <>
                                    <PropRow
                                        label={`Nome`}
                                        previewValue={customer.name}
                                        value={customer.name}
                                        change={async value => updateCustomerParam('name', value)}
                                    />
                                    <PropRow
                                        label={`E-mail`}
                                        previewValue={customer.email}
                                        value={customer.email}
                                        change={async value => updateCustomerParam('email', value)}
                                    />
                                    <PropRow
                                        label={`Telefone`}
                                        previewValue={customer.phone}
                                        value={customer.phone}
                                        change={async value => updateCustomerParam('phone', value)}
                                        mask={`convertToPhone`}
                                    />
                                    <Link className="aside-more" to={`/customer/edit/${customer.id}`}>
                                        Ficha completa da pessoa
                                        <Icon name="arrow" size={14} />
                                    </Link>
                                </>
                            }
                        </AsideSection>
                    </aside>
                </div>
            }
        </PanelTemplate>
    );
}

export default CRMCard;

// `meter` recebe o texto "80% preenchido" da API; o número vira a barrinha e o
// texto inteiro fica como tooltip.
const AsideSection = ({ icon, title, meter, children }) => {
    const [open, setOpen] = useState(true);
    const percent = parseInt(meter, 10);
    const hasMeter = !Number.isNaN(percent);

    return (
        <section className={`aside-section${open ? '' : ' is-closed'}`}>
            <button type="button" className="aside-head" onClick={() => setOpen(!open)} aria-expanded={open}>
                <span className="aside-title">
                    <Icon name={icon} size={16} />
                    {title}
                </span>
                {hasMeter &&
                    <span className="aside-meter" title={meter}>
                        <span className="aside-meter-track"><i style={{ width: `${percent}%` }} /></span>
                        <em>{percent}%</em>
                    </span>
                }
                <Icon name="arrow-dropdown" size={16} className="aside-chevron" />
            </button>
            {open &&
                <div className="aside-body">
                    {children}
                </div>
            }
        </section>
    );
}

const OrderOrigin = ({ order, item }) => {
    const isProduct = !!item;

    return (
        <section className="origin-block">
            <div className="origin-head">
                <span className="origin-kind">
                    <Icon name={isProduct ? 'product' : 'order'} size={15} />
                    {isProduct ? 'Produto do pedido' : 'Pedido de origem'}
                </span>
                {order?.status_name &&
                    <span className="origin-status">
                        <i style={{ backgroundColor: order.status_color || '#696969' }} />
                        {order.status_name}
                    </span>
                }
            </div>

            {isProduct &&
                <div className="origin-item">
                    <div className="origin-thumb">
                        <img
                            src={item.image_url?.small}
                            alt={item.product_name}
                            onError={(e) => { e.target.src = require('../../../assets/images/svgs/default.svg').default; }}
                        />
                    </div>
                    <div className="origin-item-info">
                        <strong>{item.subproduct_name || item.product_name}</strong>
                        {item.subproduct_name && item.product_name &&
                            <span>{item.product_name}</span>
                        }
                        {item.quantity != null &&
                            <span className="origin-qty">{item.quantity} unidade{item.quantity === 1 ? '' : 's'}</span>
                        }
                    </div>
                </div>
            }

            <dl className="origin-stats">
                {isProduct && item.price != null &&
                    <div>
                        <dt>Unitário</dt>
                        <dd>{convertIntToMoney(item.price, true)}</dd>
                    </div>
                }
                {isProduct && item.total != null &&
                    <div>
                        <dt>Item</dt>
                        <dd>{convertIntToMoney(item.total, true)}</dd>
                    </div>
                }
                {order?.total != null &&
                    <div className="is-total">
                        <dt>Pedido</dt>
                        <dd>{convertIntToMoney(order.total, true)}</dd>
                    </div>
                }
            </dl>

            {order &&
                <Link to={`/order/edit/${order.id}`} target="_blank" className="origin-link">
                    Abrir pedido #{order.code}
                    <Icon name="external-link" size={14} />
                </Link>
            }
        </section>
    );
}

const QuoteOrigin = ({ budget }) => {
    return (
        <section className="origin-block">
            <div className="origin-head">
                <span className="origin-kind">
                    <Icon name="quote" size={15} />
                    Orçamento de origem
                </span>
                {budget.status_name &&
                    <span className="origin-status">
                        <i style={{ backgroundColor: budget.status_color || '#696969' }} />
                        {budget.status_name}
                    </span>
                }
            </div>

            <div className="origin-item">
                <div className="origin-thumb origin-thumb-icon">
                    <Icon name="quote" size={22} />
                </div>
                <div className="origin-item-info">
                    <strong>Orçamento #{budget.code}</strong>
                    {budget.items_count != null &&
                        <span>{budget.items_count} {budget.items_count === 1 ? 'item' : 'itens'}</span>
                    }
                </div>
            </div>

            <dl className="origin-stats">
                {budget.total != null &&
                    <div className="is-total">
                        <dt>Total</dt>
                        <dd>{convertIntToMoney(budget.total, true)}</dd>
                    </div>
                }
            </dl>

            <Link to={`/quote/edit/${budget.id}`} target="_blank" className="origin-link">
                Abrir orçamento #{budget.code}
                <Icon name="external-link" size={14} />
            </Link>
        </section>
    );
}

const PropRow = ({type, label, previewValue, copyValue, value, change, options, mask, link, linkNewTab, pipelineId}) => {
    const navigate = useNavigate();

    const [edit, setEdit] = useState(false);
    const [prevValue, setPrevValue] = useState(value);
    const [updating, setUpdating] = useState(false);

    const copyContent = async () => {
        const text = copyValue ?? (typeof previewValue === 'string' ? previewValue : '');
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Texto copiado", { autoClose: 1500});
        } catch (err) {
            toast.error("Falha ao copiar o texto: ", err);
        }
    }

    const openEditor = () => {
        setPrevValue(value);
        setEdit(true);
    }

    const save = async () => {
        setEdit(false);
        setUpdating(true);
        let result = await change(prevValue);
        setUpdating(false);
        if(!result){
            setEdit(true);
        }
    }

    const editable = typeof change === 'function';

    return (
        <div className={`prop-row${editable ? ' is-editable' : ''}`}>
            {edit &&
                <div className="prop-editor" onKeyDown={e => { if (e.key === 'Escape') setEdit(false); }}>
                    {type === 'select' &&
                        <InputSelect options={options} value={prevValue} change={setPrevValue} />
                    }
                    {type === 'admin' &&
                        <SelectAdmin
                            selectedAdmin={prevValue}
                            setSelectedAdmin={setPrevValue}
                            nullable={true}
                            labelNullable={`Sem responsável`}
                            nullableValue={null}
                            pipelineId={pipelineId}
                        />
                    }
                    {(type === 'text' || !type) &&
                        <Input label={label} value={prevValue} change={setPrevValue} mask={mask ?? null} />
                    }
                    {type === 'date' &&
                        <InputDate
                            format={`YYYY-MM-DD`}
                            value={prevValue}
                            change={setPrevValue}
                            label={label}
                        />
                    }
                    <div className="prop-editor-actions">
                        <Button
                            type={`text`}
                            text={`Cancelar`}
                            size={`small`}
                            action={() => setEdit(false)}
                        />
                        <Button
                            type={`primary`}
                            text={`Salvar`}
                            size={`small`}
                            action={save}
                        />
                    </div>
                </div>
            }
            <span className="prop-label">{label}</span>
            <span className="prop-value">
                {link && linkNewTab
                    ? <a href={link} target="_blank" rel="noreferrer" className="prop-link">
                        {previewValue}
                        <Icon name="link" size={14} />
                    </a>
                    : (updating ? <span className="prop-muted">Salvando...</span> : previewValue)
                }
            </span>
            <div className="prop-actions">
                {!linkNewTab &&
                    <button type="button" title="Copiar" onClick={copyContent}><Icon name="copy" size={14} /></button>
                }
                {editable &&
                    <button type="button" title="Editar" onClick={openEditor}><Icon name="edit" size={14} /></button>
                }
                {link && !linkNewTab &&
                    <button type="button" title="Abrir" onClick={() => navigate(link)}><Icon name="arrow" size={14} /></button>
                }
            </div>
        </div>
    );
}
